import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

const StarfieldBackground = () => {
    const containerRef = useRef(null);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        // Create static stars with CSS animations for better performance
        const createStars = () => {
            // Clear existing stars
            container.innerHTML = '';

            // Create different types of stars
            const starTypes = [
                { count: 100, className: 'star star-small', delay: 0 },
                { count: 50, className: 'star star-medium', delay: 1 },
                { count: 20, className: 'star star-large', delay: 2 }
            ];

            starTypes.forEach(({ count, className, delay }) => {
                for (let i = 0; i < count; i++) {
                    const star = document.createElement('div');
                    star.className = className;
                    star.style.left = Math.random() * 100 + '%';
                    star.style.top = Math.random() * 100 + '%';
                    star.style.animationDelay = (Math.random() * 3 + delay) + 's';

                    // Add random drift animation
                    if (Math.random() > 0.7) {
                        star.classList.add('drifting-star');
                    }

                    container.appendChild(star);
                }
            });

            // Create shooting stars
            for (let i = 0; i < 3; i++) {
                const shootingStar = document.createElement('div');
                shootingStar.className = 'shooting-star';
                shootingStar.style.top = Math.random() * 50 + '%';
                shootingStar.style.animationDelay = Math.random() * 10 + 's';
                shootingStar.style.animationDuration = (Math.random() * 2 + 2) + 's';
                container.appendChild(shootingStar);
            }
        };

        createStars();

        // Recreate stars on window resize
        const handleResize = () => {
            createStars();
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    return (
        <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
            {/* CSS-based stars container */}
            <div ref={containerRef} className="absolute inset-0" />

            {/* Framer Motion enhanced particles */}
            {[...Array(15)].map((_, i) => (
                <motion.div
                    key={`motion-star-${i}`}
                    className="absolute w-1 h-1 bg-white rounded-full"
                    initial={{
                        x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1920),
                        y: Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 1080),
                        opacity: 0,
                    }}
                    animate={{
                        x: [
                            Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1920),
                            Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1920),
                        ],
                        y: [
                            Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 1080),
                            Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 1080),
                        ],
                        opacity: [0, 0.8, 0],
                        scale: [0.5, 1.2, 0.5],
                    }}
                    transition={{
                        duration: Math.random() * 8 + 6,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: Math.random() * 3,
                    }}
                />
            ))}

            {/* Constellation effect */}
            <motion.div
                className="absolute inset-0"
                initial={{ opacity: 0 }}
                animate={{ opacity: [0, 0.3, 0] }}
                transition={{
                    duration: 8,
                    repeat: Infinity,
                    ease: "easeInOut",
                }}
            >
                <svg className="w-full h-full opacity-20">
                    {[...Array(3)].map((_, i) => (
                        <motion.circle
                            key={`constellation-${i}`}
                            cx={30 + i * 25 + "%"}
                            cy={20 + i * 15 + "%"}
                            r="1"
                            fill="white"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: [0, 1, 0] }}
                            transition={{
                                duration: 4,
                                repeat: Infinity,
                                delay: i * 0.5,
                            }}
                        />
                    ))}
                </svg>
            </motion.div>

            {/* Floating particles */}
            {[...Array(25)].map((_, i) => (
                <motion.div
                    key={`particle-${i}`}
                    className="absolute w-0.5 h-0.5 rounded-full"
                    style={{
                        background: `rgba(255, 255, 255, ${Math.random() * 0.5 + 0.2})`,
                    }}
                    initial={{
                        x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1920),
                        y: (typeof window !== 'undefined' ? window.innerHeight : 1080) + 20,
                    }}
                    animate={{
                        x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1920),
                        y: -20,
                        opacity: [0, 0.6, 0],
                    }}
                    transition={{
                        duration: Math.random() * 12 + 8,
                        repeat: Infinity,
                        ease: "linear",
                        delay: Math.random() * 8,
                    }}
                />
            ))}
        </div>
    );
};

export default StarfieldBackground;